/**
 * OnCallOverview — super_admin read-only view of all admin on-call schedules.
 *
 * Renders a 7×24 weekly grid showing which admin (by initials/email) is on
 * call for each hour, plus a list of detected gap windows (where no admin is
 * scheduled). Gap detection is informational — it does not block edits.
 *
 * Source data: GET /api/v1/admin/settings/on-call-overview (super_admin only).
 *
 * @story SCRUM-1803
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import DashboardLayout from '../../components/layout/DashboardLayout';
import apiRequest from '../../services/api';

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function hourFromTime(timeStr) {
  if (typeof timeStr !== 'string') { return 0; }
  const m = /^([01]\d|2[0-3]):/.exec(timeStr);
  return m ? parseInt(m[1], 10) : 0;
}

function adminInitials(admin) {
  if (admin.name) {
    const parts = admin.name.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) { return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase(); }
    if (parts.length === 1) { return parts[0].slice(0, 2).toUpperCase(); }
  }
  if (admin.email) { return admin.email.slice(0, 2).toUpperCase(); }
  return '??';
}

// Pre-compute coverage[day][hour] = [adminIds...] from the admins list.
function buildCoverageGrid(admins) {
  const grid = Array.from({ length: 7 }, () => Array.from({ length: 24 }, () => []));
  for (const admin of admins) {
    for (const entry of (admin.entries || [])) {
      if (!entry.is_active) { continue; }
      const dow = entry.day_of_week;
      const startH = hourFromTime(entry.start_time);
      const endH = hourFromTime(entry.end_time);
      if (startH < endH) {
        for (let h = startH; h < endH; h++) {
          grid[dow][h].push(admin);
        }
      } else if (startH > endH) {
        for (let h = startH; h < 24; h++) { grid[dow][h].push(admin); }
        const next = (dow + 1) % 7;
        for (let h = 0; h < endH; h++) { grid[next][h].push(admin); }
      }
    }
  }
  return grid;
}

export default function OnCallOverview() {
  const navigate = useNavigate();
  const { user: authUser, hasRole } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [gaps, setGaps] = useState([]);

  const isSuperAdmin = typeof hasRole === 'function' && hasRole('super_admin');

  useEffect(() => {
    if (!isSuperAdmin) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await apiRequest('/api/v1/admin/settings/on-call-overview');
        if (cancelled) { return; }
        if (res && res.success && res.data) {
          setAdmins(res.data.admins || []);
          setGaps(res.data.gaps || []);
        }
      } catch (err) {
        if (!cancelled) { setError(err.message || 'Failed to load overview'); }
      } finally {
        if (!cancelled) { setLoading(false); }
      }
    })();
    return () => { cancelled = true; };
  }, [isSuperAdmin]);

  const handleNavigate = (id, path) => {
    if (path) { navigate(path); }
  };

  if (!isSuperAdmin) {
    return (
      <DashboardLayout
        userRole="admin"
        activeNavId="settings"
        onNavigate={handleNavigate}
        user={{
          name: authUser?.fullName || authUser?.firstName || 'Admin',
          email: authUser?.email || '',
          avatar: authUser?.profilePicture || null,
        }}
      >
        <div data-testid="on-call-overview-forbidden" style={{ padding: '24px' }}>
          <h1 style={{ margin: '0 0 8px', fontSize: '24px', fontWeight: 600 }}>Forbidden</h1>
          <p style={{ color: '#6b7280', fontSize: '14px' }}>
            This page is restricted to super administrators.
          </p>
        </div>
      </DashboardLayout>
    );
  }

  const grid = buildCoverageGrid(admins);

  return (
    <DashboardLayout
      userRole="admin"
      activeNavId="settings"
      onNavigate={handleNavigate}
      user={{
        name: authUser?.fullName || authUser?.firstName || 'Admin',
        email: authUser?.email || '',
        avatar: authUser?.profilePicture || null,
      }}
    >
      <div data-testid="on-call-overview-page" style={{ padding: '0 0 24px' }}>
        <h1 style={{ margin: '0 0 8px', fontSize: '24px', fontWeight: 600 }}>
          On-Call Overview
        </h1>
        <p style={{ margin: '0 0 16px', color: '#6b7280', fontSize: '14px' }}>
          All administrators&apos; configured on-call windows. Gaps below indicate hours
          with no admin scheduled.
        </p>

        {loading && <p style={{ color: '#6b7280' }}>Loading…</p>}

        {error && (
          <p data-testid="overview-error" style={{ color: '#b91c1c' }} role="alert">{error}</p>
        )}

        {!loading && !error && admins.length === 0 && (
          <p
            data-testid="empty-overview"
            style={{
              backgroundColor: '#fef3c7',
              border: '1px solid #f59e0b',
              color: '#92400e',
              padding: '10px 12px',
              borderRadius: '8px',
              fontSize: '14px',
            }}
          >
            No on-call schedules configured. All admins receive every ops alert.
          </p>
        )}

        {!loading && !error && admins.length > 0 && (
          <>
            <div data-testid="coverage-grid" style={{ overflowX: 'auto', marginBottom: '24px' }}>
              <table style={{ borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead>
                  <tr>
                    <th style={{ padding: '4px 8px', borderBottom: '1px solid #d1d5db', textAlign: 'left' }}>Day</th>
                    {Array.from({ length: 24 }, (_, h) => (
                      <th key={h} style={{ padding: '4px 6px', borderBottom: '1px solid #d1d5db', minWidth: '24px' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {DAY_LABELS.map((label, d) => (
                    <tr key={label}>
                      <td style={{ padding: '4px 8px', fontWeight: 600, borderRight: '1px solid #e5e7eb' }}>{label}</td>
                      {Array.from({ length: 24 }, (_, h) => {
                        const cell = grid[d][h];
                        const covered = cell.length > 0;
                        return (
                          <td
                            key={h}
                            data-testid={`cell-${d}-${h}`}
                            title={covered ? cell.map(a => a.email || a.userId).join(', ') : 'No coverage'}
                            style={{
                              padding: '4px 4px',
                              border: '1px solid #f3f4f6',
                              backgroundColor: covered ? '#dcfce7' : '#fee2e2',
                              textAlign: 'center',
                              fontSize: '11px',
                              color: covered ? '#15803d' : '#b91c1c',
                            }}
                          >
                            {covered ? cell.map(adminInitials).join(',') : '—'}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h2 style={{ fontSize: '16px', margin: '0 0 8px' }}>Admins</h2>
            <ul data-testid="admin-list" style={{ listStyle: 'none', padding: 0, margin: '0 0 24px' }}>
              {admins.map(a => (
                <li
                  key={a.userId}
                  data-testid={`admin-${a.userId}`}
                  style={{ padding: '4px 0', fontSize: '14px' }}
                >
                  <strong>{a.name || a.email}</strong>{' '}
                  <span style={{ color: '#6b7280' }}>
                    ({a.entries.length} window{a.entries.length === 1 ? '' : 's'})
                  </span>
                </li>
              ))}
            </ul>

            <h2 style={{ fontSize: '16px', margin: '0 0 8px' }}>Coverage gaps</h2>
            {gaps.length === 0 ? (
              <p data-testid="no-gaps" style={{ color: '#047857', fontSize: '14px' }}>
                No gaps detected — every hour of the week has at least one admin on-call.
              </p>
            ) : (
              <ul data-testid="gap-list" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {gaps.map((g, i) => (
                  <li
                    key={`${g.day_of_week}-${g.start_hour}-${i}`}
                    data-testid={`gap-${g.day_of_week}-${g.start_hour}`}
                    style={{
                      backgroundColor: '#fef3c7',
                      border: '1px solid #f59e0b',
                      borderRadius: '6px',
                      padding: '6px 10px',
                      margin: '0 0 6px',
                      color: '#92400e',
                      fontSize: '13px',
                    }}
                  >
                    {g.day_label || DAY_LABELS[g.day_of_week]}: {String(g.start_hour).padStart(2, '0')}:00 — {String(g.end_hour).padStart(2, '0')}:00
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
